import { Component, inject, OnInit } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FileInfo } from "src/app/common/components/file-content-list/file-content-list.component";
import { ConfigService } from "src/app/common/services/config.service";
import { FloatingAssistantChatService } from "src/app/common/services/floating-assistant-chat.service";
import { LlmConfigItem, LlmConfigService } from "src/app/common/services/llm-config.service";

@Component({
  templateUrl: "./assistant-settings.component.html",
  styleUrls: ["./assistant-settings.component.scss"],
})
export class AssistantSettingsComponent implements OnInit {
  protected readonly ref = inject(DynamicDialogRef);
  protected readonly floatingAssistantChatService = inject(FloatingAssistantChatService);
  protected readonly configService = inject(ConfigService);
  protected readonly dialogConfig = inject(DynamicDialogConfig);
  protected readonly llmConfigService = inject(LlmConfigService);

  model!: string;
  temperature!: number;
  context!: string;
  contextFiles: FileInfo[] = [];
  optionsModel: object[] = [];
  LLMsDescriptions = Object.entries(this.llmConfigService.llmConfigs)
    .map(([key, llm]: [string, LlmConfigItem]) => `<LI><B>${llm.displayedName}</B>: ${llm.description}`)
    .join("\n");

  // Whether “Ancrage des recherches” is checked
  searchGrounding = false;

  // Whether the currently‐selected model may actually search
  searchEnabled = false;

  async ngOnInit(): Promise<void> {
    this.model = this.dialogConfig.data.model || this.configService.defaultLlm;
    this.temperature = this.dialogConfig.data.temperature;
    this.context = this.dialogConfig.data.context;
    this.contextFiles = this.dialogConfig.data.contextFiles;
    this.searchGrounding = Boolean(this.dialogConfig.data.searchGrounding);
    this.optionsModel = await this.llmConfigService.getLlmOptions();
    await this.updateSearchEnabled();
  }

  saveSettings(): void {
    this.ref.close({
      save: true,
      temperature: this.temperature,
      model: this.model,
      context: this.context,
      contextFiles: this.contextFiles,
      searchGrounding: this.searchGrounding,
    });
  }

  onFileSelected(files: FileInfo[]): void {
    const selectedFileIds = new Set(files.map((file) => file.id));
    this.contextFiles = this.contextFiles.filter((file) => !selectedFileIds.has(file.id));
    this.contextFiles = this.contextFiles.concat(files);
  }

  removeContextFile(id: string): void {
    this.contextFiles = this.contextFiles.filter((file) => file.id !== id);
  }

  async updateSearchEnabled(): Promise<void> {
    this.searchEnabled = await this.llmConfigService.isModelSearchEnabled(this.model);
    // If the model no longer allows searching, force‐uncheck:
    if (!this.searchEnabled) {
      this.searchGrounding = false;
    }
  }

  onModelChange(newValue: string): void {
    this.model = newValue;
    // Update whether search is allowed with the selected model
    void this.updateSearchEnabled().then(() => {
      // Always reset checkbox state when switching models, even if both support search
      this.searchGrounding = false;
    });
  }

  get selectedModelDisplayName(): string {
    const item = Object.values(this.llmConfigService.llmConfigs).find((m) => m.technicalName === this.model);
    return item ? item.displayedName : this.model;
  }
}
